// TailorWorkOrdersTab.jsx — Phase 2D
// 3-section list (Overdue / Today / Active), 6-stage status stepper,
// full new order creation flow, WhatsApp notifications, customer measurements link.

import { useState, useEffect, useCallback, useMemo } from "react";

const API = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";

// ─── Status config ────────────────────────────────────────────────────────
const STATUSES = ["taken", "cutting", "stitching", "finishing", "ready", "delivered"];
const STATUS_CFG = {
  taken:      { label: "Taken",      color: "#3b82f6", bg: "#eff6ff" },
  cutting:    { label: "Cutting",    color: "#f97316", bg: "#fff7ed" },
  stitching:  { label: "Stitching",  color: "#8b5cf6", bg: "#f5f3ff" },
  finishing:  { label: "Finishing",  color: "#d97706", bg: "#fffbeb" },
  ready:      { label: "Ready",      color: "#16a34a", bg: "#f0fdf4" },
  delivered:  { label: "Delivered",  color: "#888",    bg: "#f5f5f5" },
};

function nextStatus(current) {
  const idx = STATUSES.indexOf(current);
  return idx >= 0 && idx < STATUSES.length - 1 ? STATUSES[idx + 1] : null;
}

function formatDate(str) {
  if (!str) return "";
  const d = new Date(str + "T00:00:00");
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function todayStr() {
  return new Date().toISOString().split("T")[0];
}

function isOverdue(wo) {
  if (!wo.deliveryDate || wo.status === "delivered") return false;
  return wo.deliveryDate < todayStr();
}

function isToday(wo) {
  return wo.deliveryDate === todayStr();
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

// ─── Main Component ────────────────────────────────────────────────────────
export default function TailorWorkOrdersTab({ business, catalogue, items: itemsProp }) {
  const [orders, setOrders]         = useState([]);
  const [customers, setCustomers]   = useState([]);
  const [catalogueItems, setCatalogueItems] = useState(itemsProp || []);
  const [loading, setLoading]       = useState(true);
  const [showNew, setShowNew]       = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [viewMeasurementsFor, setViewMeasurementsFor] = useState(null); // customer object

  const businessId = business?.businessId;

  // ── Load orders ──────────────────────────────────────────────────────────
  const loadOrders = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const res  = await fetch(`${API}/workorders?businessId=${businessId}`);
      const data = await res.json();
      // Lambda returns both keys; tailor orders have orderType: "tailor"
      const all = (data.workorders || data.workOrders || [])
        .filter(w => w.orderType === "tailor" || w.customerId);
      setOrders(all);
    } catch (e) { console.error("loadOrders", e); }
    finally { setLoading(false); }
  }, [businessId]);

  // ── Load customers (for new order picker + measurements view) ─────────────
  const loadCustomers = useCallback(async () => {
    if (!businessId) return;
    try {
      const res  = await fetch(`${API}/customers?businessId=${businessId}`);
      const data = await res.json();
      setCustomers(data.customers || []);
    } catch (e) { console.error("loadCustomers", e); }
  }, [businessId]);

  // ── Load catalogue items (designs) ──────────────────────────────────────
  const loadCatalogueItems = useCallback(async () => {
    const catalogueId = catalogue?.catalogueId;
    if (!catalogueId) return;
    // If parent already gave us items, use them but still verify
    try {
      const res  = await fetch(`${API}/items?catalogueId=${catalogueId}`);
      const data = await res.json();
      if (data.items?.length > 0) setCatalogueItems(data.items);
    } catch (e) { console.error("loadCatalogueItems", e); }
  }, [catalogue?.catalogueId]);

  useEffect(() => { loadOrders(); loadCustomers(); loadCatalogueItems(); }, [loadOrders, loadCustomers, loadCatalogueItems]);

  // ── Status advance ────────────────────────────────────────────────────────
  const advanceStatus = async (wo, targetStatus) => {
    setUpdatingId(wo.workOrderId);
    try {
      const res = await fetch(`${API}/workorders/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workOrderId: wo.workOrderId, targetStatus }),
      });
      const data = await res.json();
      if (data.success) {
        setOrders(prev => prev.map(w =>
          w.workOrderId === wo.workOrderId
            ? { ...w, status: data.status, updatedAt: new Date().toISOString() }
            : w
        ));
      }
    } catch (e) { alert("Failed to update status"); }
    finally { setUpdatingId(null); }
  };

  // ── WhatsApp message ──────────────────────────────────────────────────────
  const openWhatsApp = (wo) => {
    const phone   = (wo.customerPhone || "").replace(/\D/g, "");
    const design  = wo.designs?.[0];
    const dName   = design?.name || "your garment";
    const variant = design?.variantSelected ? ` (${design.variantSelected})` : "";
    const status  = STATUS_CFG[wo.status]?.label || wo.status;
    const shop    = business?.businessName || "our shop";
    const isReady = wo.status === "ready";

    let msg = `Hi ${wo.customerName}, your *${dName}*${variant} is now *${status}*.`;
    if (isReady) msg += `\nPlease collect from ${shop}.`;
    if (wo.designs?.length > 1) msg += `\n(+${wo.designs.length - 1} more items)`;
    msg += `\nOrder: ${wo.orderNumber}`;

    const url = phone
      ? `https://wa.me/91${phone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  };

  // ── Partition orders into sections ────────────────────────────────────────
  const { overdue, today, active } = useMemo(() => {
    const overdue = orders.filter(isOverdue).sort((a, b) => a.deliveryDate.localeCompare(b.deliveryDate));
    const today   = orders.filter(isToday);
    const active  = orders
      .filter(w => !isOverdue(w) && !isToday(w) && w.status !== "delivered")
      .sort((a, b) => (a.deliveryDate || "9999").localeCompare(b.deliveryDate || "9999"));
    return { overdue, today, active };
  }, [orders]);

  const delivered = useMemo(() =>
    orders.filter(w => w.status === "delivered")
      .sort((a, b) => (b.deliveredAt || b.updatedAt || "").localeCompare(a.deliveredAt || a.updatedAt || "")),
  [orders]);

  // ── Find customer object by ID ─────────────────────────────────────────────
  const findCustomer = (customerId) => customers.find(c => c.customerId === customerId);

  const sharedCardProps = { advanceStatus, updatingId, openWhatsApp, expandedId, setExpandedId, findCustomer, setViewMeasurementsFor };

  return (
    <div style={{ paddingBottom: 100 }}>
      {/* Header */}
      <div style={st.header}>
        <div>
          <div style={st.title}>Work Orders</div>
          <div style={st.subtitle}>{orders.filter(w => w.status !== "delivered").length} active</div>
        </div>
        <button style={st.addBtn} onClick={() => setShowNew(true)}>+ New Order</button>
      </div>

      {loading ? (
        <div style={st.empty}>Loading…</div>
      ) : orders.length === 0 ? (
        <div style={st.empty}>No work orders yet. Tap + New Order to start.</div>
      ) : (
        <>
          {/* ── Overdue ── */}
          {overdue.length > 0 && (
            <Section title={`🔴 Overdue (${overdue.length})`} accent="#dc2626" defaultOpen>
              {overdue.map(wo => <WorkOrderCard key={wo.workOrderId} wo={wo} {...sharedCardProps} />)}
            </Section>
          )}

          {/* ── Today ── */}
          {today.length > 0 && (
            <Section title={`📅 Today's Deliveries (${today.length})`} accent="#d97706" defaultOpen>
              {today.map(wo => <WorkOrderCard key={wo.workOrderId} wo={wo} {...sharedCardProps} />)}
            </Section>
          )}

          {/* ── Active ── */}
          {active.length > 0 && (
            <Section title={`Active (${active.length})`} accent="#6366f1" defaultOpen>
              {active.map(wo => <WorkOrderCard key={wo.workOrderId} wo={wo} {...sharedCardProps} />)}
            </Section>
          )}

          {/* ── Delivered ── */}
          {delivered.length > 0 && (
            <Section title={`Delivered (${delivered.length})`} accent="#888" defaultOpen={false}>
              {delivered.map(wo => <WorkOrderCard key={wo.workOrderId} wo={wo} {...sharedCardProps} />)}
            </Section>
          )}
        </>
      )}

      {/* New order drawer */}
      {showNew && (
        <NewOrderDrawer
          business={business}
          catalogueItems={catalogueItems}
          customers={customers}
          onClose={() => setShowNew(false)}
          onCreate={(newOrder) => {
            setOrders(prev => [newOrder, ...prev]);
            setShowNew(false);
            setExpandedId(newOrder.workOrderId);
          }}
        />
      )}

      {/* Customer measurements side-drawer */}
      {viewMeasurementsFor && (
        <MeasurementsDrawer
          customer={viewMeasurementsFor}
          onClose={() => setViewMeasurementsFor(null)}
        />
      )}
    </div>
  );
}

// ─── Collapsible Section ────────────────────────────────────────────────────
function Section({ title, accent, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 4 }}>
      <div onClick={() => setOpen(o => !o)} style={{ ...st.sectionHeader, borderLeft: `3px solid ${accent}` }}>
        <span style={{ ...st.sectionTitle, color: accent }}>{title}</span>
        <span style={{ fontSize: 14, color: "#aaa" }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div style={{ padding: "0 16px" }}>{children}</div>}
    </div>
  );
}

// ─── Work Order Card ────────────────────────────────────────────────────────
function WorkOrderCard({ wo, advanceStatus, updatingId, openWhatsApp, expandedId, setExpandedId, findCustomer, setViewMeasurementsFor }) {
  const cfg        = STATUS_CFG[wo.status] || STATUS_CFG.taken;
  const next       = nextStatus(wo.status);
  const isExpanded = expandedId === wo.workOrderId;
  const isUpdating = updatingId === wo.workOrderId;
  const overdue    = isOverdue(wo);
  const todayDel   = isToday(wo);
  const design     = wo.designs?.[0];
  const customer   = findCustomer(wo.customerId);

  const daysUntil = wo.deliveryDate
    ? Math.ceil((new Date(wo.deliveryDate + "T00:00:00") - new Date()) / 86400000)
    : null;

  return (
    <div style={{
      ...st.card,
      borderLeft: `4px solid ${overdue ? "#dc2626" : todayDel ? "#d97706" : cfg.color}`,
    }}>
      {/* Card header — always visible */}
      <div style={st.cardHeader} onClick={() => setExpandedId(isExpanded ? null : wo.workOrderId)}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Order ref + date */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
            <span style={st.orderRef}>{wo.orderNumber}</span>
            {wo.deliveryDate && (
              <span style={{
                fontSize: 11, fontWeight: 600,
                color: overdue ? "#dc2626" : todayDel ? "#d97706" : "#888",
              }}>
                📅 {formatDate(wo.deliveryDate)}
                {overdue && ` · ${Math.abs(daysUntil)}d overdue`}
                {todayDel && " · Today"}
                {!overdue && !todayDel && daysUntil !== null && daysUntil <= 3 && ` · ${daysUntil}d left`}
              </span>
            )}
          </div>

          {/* Customer name */}
          <div style={st.customerName}>{wo.customerName}</div>

          {/* Design summary */}
          {design && (
            <div style={st.designSummary}>
              {design.name}
              {design.variantSelected ? ` · ${design.variantSelected}` : ""}
              {wo.designs?.length > 1 ? ` +${wo.designs.length - 1} more` : ""}
            </div>
          )}

          {/* 6-dot progress */}
          <div style={st.progressRow}>
            {STATUSES.map((s, i) => {
              const statusIdx   = STATUSES.indexOf(wo.status);
              const isDone      = i < statusIdx;
              const isCurrent   = i === statusIdx;
              return (
                <div key={s} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                  <div style={{
                    width: isCurrent ? 10 : 8,
                    height: isCurrent ? 10 : 8,
                    borderRadius: "50%",
                    background: isDone || isCurrent ? cfg.color : "#e5e7eb",
                    border: isCurrent ? `2px solid ${cfg.color}` : "none",
                    boxShadow: isCurrent ? `0 0 0 2px ${cfg.bg}` : "none",
                    transition: "all 0.2s",
                  }} />
                </div>
              );
            })}
            <span style={{ ...st.statusLabel, color: cfg.color, background: cfg.bg }}>
              {cfg.label}
            </span>
          </div>
        </div>

        <div style={{ color: "#bbb", fontSize: 18, flexShrink: 0 }}>{isExpanded ? "▴" : "▾"}</div>
      </div>

      {/* Expanded detail */}
      {isExpanded && (
        <div style={st.detail}>
          {/* All designs */}
          {wo.designs?.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={st.detailLabel}>Designs</div>
              {wo.designs.map((d, i) => (
                <div key={i} style={st.designRow}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#111" }}>{d.name}</div>
                    {d.variantSelected && <div style={{ fontSize: 12, color: "#666" }}>Colour: {d.variantSelected}</div>}
                    {d.notes && <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>Note: {d.notes}</div>}
                  </div>
                  <div style={{ fontSize: 11, color: "#aaa", textAlign: "right" }}>
                    {d.category}<br />{d.measurementUnit}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Status history */}
          <div style={{ marginBottom: 12 }}>
            <div style={st.detailLabel}>Progress</div>
            <div style={st.historyRow}>
              {(wo.statusHistory || []).map((h, i) => (
                <div key={i} style={st.historyItem}>
                  <div style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: STATUS_CFG[h.status]?.color || "#888",
                    marginTop: 3, flexShrink: 0,
                  }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#333", textTransform: "capitalize" }}>{h.status}</div>
                    <div style={{ fontSize: 11, color: "#aaa" }}>{timeAgo(h.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          {wo.notes && (
            <div style={st.notesBox}>📝 {wo.notes}</div>
          )}

          {/* Customer measurements link */}
          {customer && (
            <button
              onClick={() => setViewMeasurementsFor(customer)}
              style={st.measurementsLink}
            >
              📐 View {wo.customerName.split(" ")[0]}'s measurements
            </button>
          )}

          {/* WhatsApp + Status advance */}
          <div style={st.actionRow}>
            <button
              onClick={() => openWhatsApp(wo)}
              style={st.whatsappBtn}
            >
              💬 WhatsApp
            </button>

            {next && (
              <button
                onClick={() => advanceStatus(wo, next)}
                disabled={isUpdating}
                style={{
                  ...st.advanceBtn,
                  background: STATUS_CFG[next]?.color || "#111",
                  opacity: isUpdating ? 0.5 : 1,
                }}
              >
                {isUpdating ? "Updating…" : `→ ${STATUS_CFG[next]?.label}`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── New Order Drawer — 3-step flow ────────────────────────────────────────
export function NewOrderDrawer({ business, catalogueItems, customers, onClose, onCreate, initialDesigns, initialCustomer }) {
  const [step, setStep] = useState(() => {
    // If we have pre-seeded designs AND a customer, start at step 3
    if (initialDesigns?.length > 0 && initialCustomer) return 3;
    // If we only have designs, start at step 1 (pick customer)
    return 1;
  });
  const [selectedCustomer, setSelectedCustomer] = useState(initialCustomer || null);
  const [selectedDesigns, setSelectedDesigns]   = useState(initialDesigns || []);
  const [deliveryDate, setDeliveryDate]         = useState("");
  const [notes, setNotes]                       = useState("");
  const [saving, setSaving]                     = useState(false);
  const [customerSearch, setCustomerSearch]     = useState("");
  // Inline customer creation state
  const [showInlineCreate, setShowInlineCreate] = useState(false);
  const [inlineName, setInlineName]             = useState("");
  const [inlinePhone, setInlinePhone]           = useState("");
  const [inlineSaving, setInlineSaving]         = useState(false);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(c =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.phone || "").includes(q)
    );
  }, [customers, customerSearch]);

  const toggleDesign = (item) => {
    setSelectedDesigns(prev => {
      const exists = prev.find(d => d.itemId === item.itemId);
      if (exists) return prev.filter(d => d.itemId !== item.itemId);
      return [...prev, {
        itemId:          item.itemId,
        name:            item.name,
        category:        item.category || "",
        variantSelected: item.variants?.[0]?.name || "",
        notes:           "",
        measurements_snapshot: selectedCustomer?.measurements || {},
        measurementUnit: selectedCustomer?.measurementUnit || "in",
      }];
    });
  };

  const updateDesignVariant = (itemId, variant) => {
    setSelectedDesigns(prev => prev.map(d => d.itemId === itemId ? { ...d, variantSelected: variant } : d));
  };

  const updateDesignNote = (itemId, note) => {
    setSelectedDesigns(prev => prev.map(d => d.itemId === itemId ? { ...d, notes: note } : d));
  };

  // Inline new customer creation (Step 1)
  const handleInlineCreate = async () => {
    if (!inlineName.trim() || !inlinePhone.trim()) return;
    setInlineSaving(true);
    try {
      const res = await fetch(`${API}/customers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessId: business.businessId,
          name:       inlineName.trim(),
          phone:      inlinePhone.trim(),
          tags:       [],
          measurementUnit: "in",
          measurements: {},
          notes: "",
        }),
      });
      if (!res.ok) throw new Error("Failed to create");
      const data = await res.json();
      const newCustomer = data.customer || {
        customerId: data.customerId,
        name:       inlineName.trim(),
        phone:      inlinePhone.trim(),
        tags:       [],
        measurements: {},
        measurementUnit: "in",
      };
      setSelectedCustomer(newCustomer);
      setShowInlineCreate(false);
      setInlineName(""); setInlinePhone("");
    } catch (e) { alert("Could not create customer: " + e.message); }
    finally { setInlineSaving(false); }
  };

  const handleCreate = async () => {
    if (!selectedCustomer || selectedDesigns.length === 0) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/workorders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessId:    business.businessId,
          orderType:     "tailor",
          customerId:    selectedCustomer.customerId,
          customerName:  selectedCustomer.name,
          customerPhone: selectedCustomer.phone || "",
          designs:       selectedDesigns,
          deliveryDate:  deliveryDate || null,
          notes:         notes.trim(),
        }),
      });
      const data = await res.json();
      if (data.workOrder || data.workorder) {
        onCreate(data.workOrder || data.workorder);
      } else {
        alert("Failed to create order");
      }
    } catch (e) { alert("Error: " + e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={st.drawerBackdrop} onClick={onClose}>
      <div style={st.drawer} onClick={e => e.stopPropagation()}>
        {/* Drawer header */}
        <div style={st.drawerHeader}>
          <div>
            <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
              New Work Order · Step {step} of 3
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111", marginTop: 2 }}>
              {step === 1 ? "Pick Customer" : step === 2 ? "Pick Designs" : "Delivery Details"}
            </div>
          </div>
          <button onClick={onClose} style={st.closeBtn}>✕</button>
        </div>

        {/* Step progress bar */}
        <div style={{ display: "flex", gap: 4, padding: "0 16px 12px" }}>
          {[1, 2, 3].map(n => (
            <div key={n} style={{
              flex: 1, height: 3, borderRadius: 2,
              background: n <= step ? "#6366f1" : "#e5e7eb",
              transition: "background 0.2s",
            }} />
          ))}
        </div>

        {/* Step 1 — Customer picker */}
        {step === 1 && (
          <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px" }}>
            {/* Inline create form */}
            {showInlineCreate ? (
              <div style={{ background: "#f5f3ff", borderRadius: 12, padding: 14, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#6366f1", marginBottom: 10 }}>
                  New Customer
                </div>
                <input
                  autoFocus
                  value={inlineName}
                  onChange={e => setInlineName(e.target.value)}
                  placeholder="Customer name"
                  style={{ ...st.input, marginBottom: 8 }}
                />
                <input
                  value={inlinePhone}
                  onChange={e => setInlinePhone(e.target.value)}
                  placeholder="Phone number"
                  inputMode="tel"
                  style={{ ...st.input, marginBottom: 10 }}
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => { setShowInlineCreate(false); setInlineName(""); setInlinePhone(""); }}
                    style={st.backBtn}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleInlineCreate}
                    disabled={inlineSaving || !inlineName.trim() || !inlinePhone.trim()}
                    style={{
                      ...st.nextBtn,
                      opacity: (inlineSaving || !inlineName.trim() || !inlinePhone.trim()) ? 0.4 : 1,
                    }}
                  >
                    {inlineSaving ? "Saving…" : "Save & Select"}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowInlineCreate(true)}
                style={st.newCustomerBtn}
              >
                ＋ New Customer
              </button>
            )}

            <input
              value={customerSearch}
              onChange={e => setCustomerSearch(e.target.value)}
              placeholder="Search existing customers…"
              style={{ ...st.input, marginBottom: 12 }}
            />
            {filteredCustomers.length === 0 && !showInlineCreate && (
              <div style={st.empty}>No customers found. Use + New Customer above.</div>
            )}
            {filteredCustomers.map(c => {
              const isSelected = selectedCustomer?.customerId === c.customerId;
              const MKEYS = ["shoulder","chest","waist","hip","sleeveLength","sleeveCircum","bicep","armhole","bottomWaist","bottomHip","bottomThigh","bottomKnee","bottomLength","neck","length"];
              const filled = MKEYS.filter(k => (c.measurements || {})[k] != null && (c.measurements || {})[k] !== "").length;
              return (
                <div
                  key={c.customerId}
                  onClick={() => setSelectedCustomer(c)}
                  style={{
                    ...st.pickerRow,
                    ...(isSelected ? st.pickerRowSelected : {}),
                  }}
                >
                  <div style={st.customerAvatar}>
                    {(c.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#111" }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>{c.phone}</div>
                    <div style={{ fontSize: 11, color: filled > 0 ? "#16a34a" : "#aaa", marginTop: 2 }}>
                      {filled > 0 ? `✓ ${filled}/15 measurements` : "No measurements on file"}
                    </div>
                  </div>
                  {isSelected && <div style={{ color: "#6366f1", fontSize: 20 }}>✓</div>}
                </div>
              );
            })}
          </div>
        )}

        {step === 2 && (
          <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px" }}>
            {catalogueItems.length === 0 && (
              <div style={st.empty}>No designs in catalogue. Add them in the Designs tab first.</div>
            )}
            {catalogueItems.map(item => {
              const isSelected = !!selectedDesigns.find(d => d.itemId === item.itemId);
              const selDesign  = selectedDesigns.find(d => d.itemId === item.itemId);
              // Normalise variants to array of strings regardless of schema shape
              const variantNames = (item.variants || [])
                .map(v => (typeof v === "string" ? v : v?.name || v?.colour || v?.color || ""))
                .filter(Boolean);
              const thumb = Array.isArray(item.photos) ? item.photos[0] : null;

              return (
                <div key={item.itemId} style={{ marginBottom: 10 }}>
                  <div
                    onClick={() => toggleDesign(item)}
                    style={{
                      ...st.pickerRow,
                      ...(isSelected ? st.pickerRowSelected : {}),
                    }}
                  >
                    {thumb ? (
                      <img src={thumb} alt={item.name || ""} style={st.designThumb} />
                    ) : (
                      <div style={{ ...st.designThumb, background: "#f5f5f3", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                        👗
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#111" }}>{item.name || "Unnamed"}</div>
                      <div style={{ fontSize: 12, color: "#888" }}>{item.category || ""}</div>
                    </div>
                    {isSelected && <div style={{ color: "#6366f1", fontSize: 20, flexShrink: 0 }}>✓</div>}
                  </div>

                  {/* Variant + note panel for selected design */}
                  {isSelected && selDesign && (
                    <div
                      style={{ background: "#f5f3ff", borderRadius: 10, padding: "10px 12px", marginTop: 4 }}
                      onClick={e => e.stopPropagation()}
                    >
                      {variantNames.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#6366f1", marginBottom: 6, textTransform: "uppercase" }}>
                            Colour / Variant
                          </div>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {variantNames.map(vName => (
                              <button
                                key={vName}
                                onClick={() => updateDesignVariant(item.itemId, vName)}
                                style={{
                                  padding: "4px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
                                  border: "1.5px solid",
                                  borderColor: selDesign.variantSelected === vName ? "#6366f1" : "#ddd",
                                  background:  selDesign.variantSelected === vName ? "#6366f1" : "#fff",
                                  color:       selDesign.variantSelected === vName ? "#fff"    : "#666",
                                  cursor: "pointer",
                                }}
                              >
                                {vName}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#6366f1", marginBottom: 4, textTransform: "uppercase" }}>
                          Design Notes
                        </div>
                        <input
                          value={selDesign.notes || ""}
                          onChange={e => updateDesignNote(item.itemId, e.target.value)}
                          placeholder="e.g. Princess seams, back zip…"
                          style={{ ...st.input, fontSize: 13, padding: "8px 10px" }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Step 3 — Delivery details */}
        {step === 3 && (
          <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px" }}>
            {/* Summary */}
            <div style={st.summaryBox}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#111", marginBottom: 4 }}>
                {selectedCustomer?.name}
              </div>
              <div style={{ fontSize: 13, color: "#666" }}>
                {selectedDesigns.map(d => d.name + (d.variantSelected ? ` (${d.variantSelected})` : "")).join(", ")}
              </div>
              {selectedCustomer?.measurements && Object.values(selectedCustomer.measurements).some(v => v != null) && (
                <div style={{ fontSize: 11, color: "#16a34a", marginTop: 4 }}>✓ Measurements auto-attached</div>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={st.detailLabel}>Delivery Date</div>
              <input
                type="date"
                value={deliveryDate}
                onChange={e => setDeliveryDate(e.target.value)}
                min={todayStr()}
                style={st.input}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={st.detailLabel}>Order Notes</div>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Any special instructions, occasion details…"
                rows={3}
                style={{ ...st.input, resize: "vertical" }}
              />
            </div>
          </div>
        )}

        {/* Footer buttons */}
        <div style={st.drawerFooter}>
          {step > 1 && (
            <button onClick={() => setStep(s => s - 1)} style={st.backBtn}>← Back</button>
          )}
          {step < 3 && (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={step === 1 ? !selectedCustomer : selectedDesigns.length === 0}
              style={{
                ...st.nextBtn,
                opacity: (step === 1 ? !selectedCustomer : selectedDesigns.length === 0) ? 0.4 : 1,
              }}
            >
              Next →
            </button>
          )}
          {step === 3 && (
            <button
              onClick={handleCreate}
              disabled={saving}
              style={{ ...st.nextBtn, opacity: saving ? 0.5 : 1 }}
            >
              {saving ? "Creating…" : "Create Work Order"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Customer Measurements Drawer (read-only) ──────────────────────────────
const MEASUREMENT_LABELS = {
  shoulder: "Shoulder", chest: "Chest", waist: "Waist", hip: "Hip",
  sleeveLength: "Sleeve Len", sleeveCircum: "Sleeve Round", bicep: "Bicep", armhole: "Armhole",
  bottomWaist: "Pant Waist", bottomHip: "Pant Hip", bottomThigh: "Pant Thigh",
  bottomKnee: "Pant Knee", bottomLength: "Pant Length", neck: "Neck", length: "Length",
};

function MeasurementsDrawer({ customer, onClose }) {
  const m    = customer.measurements || {};
  const unit = customer.measurementUnit || "in";
  const keys = Object.keys(MEASUREMENT_LABELS);

  return (
    <div style={{ ...st.drawerBackdrop, zIndex: 1100 }} onClick={onClose}>
      <div style={{ ...st.drawer, height: "80vh" }} onClick={e => e.stopPropagation()}>
        <div style={st.drawerHeader}>
          <div>
            <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Measurements ({unit})
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111", marginTop: 2 }}>
              {customer.name}
            </div>
          </div>
          <button onClick={onClose} style={st.closeBtn}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
          {/* Fit notes */}
          {m.notes && (
            <div style={{ ...st.notesBox, marginBottom: 12 }}>
              ✂️ {m.notes}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {keys.map(key => {
              const val = m[key];
              const filled = val != null && val !== "";
              return (
                <div key={key} style={{
                  background: filled ? "#f9fdf9" : "#fafafa",
                  border: `1.5px solid ${filled ? "#bbf7d0" : "#efefed"}`,
                  borderRadius: 10, padding: "8px 10px",
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 4 }}>
                    {MEASUREMENT_LABELS[key]}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: filled ? "#111" : "#ccc", fontVariantNumeric: "tabular-nums" }}>
                    {filled ? val : "—"}
                    {filled && <span style={{ fontSize: 10, fontWeight: 600, color: "#888", marginLeft: 2 }}>{unit}</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Customer notes */}
          {customer.notes && (
            <div style={{ ...st.notesBox, marginTop: 12 }}>📝 {customer.notes}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────
const st = {
  header:   { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 16px 12px" },
  title:    { fontSize: 18, fontWeight: 700, color: "#111" },
  subtitle: { fontSize: 12, color: "#888", marginTop: 2 },
  addBtn:   { background: "#111", color: "#fff", border: "none", borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  empty:    { padding: "40px 16px", textAlign: "center", color: "#aaa", fontSize: 14 },

  sectionHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", cursor: "pointer", marginBottom: 8 },
  sectionTitle:  { fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 },

  card:        { background: "#fff", borderRadius: 14, marginBottom: 10, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", border: "1px solid #f0f0f0" },
  cardHeader:  { display: "flex", alignItems: "flex-start", padding: "12px 14px", cursor: "pointer", gap: 8 },
  orderRef:    { fontSize: 12, fontWeight: 800, fontFamily: "monospace", color: "#6366f1", letterSpacing: 0.5 },
  customerName:{ fontSize: 15, fontWeight: 700, color: "#111", marginBottom: 2 },
  designSummary:{ fontSize: 12, color: "#666", marginBottom: 6 },
  progressRow: { display: "flex", alignItems: "center", gap: 5, marginTop: 4 },
  statusLabel: { fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 8, textTransform: "uppercase", letterSpacing: 0.3, marginLeft: 4 },

  detail:      { borderTop: "1px solid #f5f5f5", padding: "12px 14px 14px" },
  detailLabel: { fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 },
  designRow:   { display: "flex", gap: 10, padding: "8px 0", borderBottom: "1px solid #f5f5f5" },
  historyRow:  { display: "flex", gap: 12, flexWrap: "wrap" },
  historyItem: { display: "flex", gap: 6, alignItems: "flex-start" },
  notesBox:    { fontSize: 13, color: "#666", background: "#f9f9f9", borderRadius: 8, padding: "8px 12px", marginBottom: 10 },
  measurementsLink: { display: "block", width: "100%", padding: "9px 12px", marginBottom: 10, borderRadius: 10, border: "1.5px solid #c7d2fe", background: "#eef2ff", color: "#4338ca", fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "left" },
  actionRow:   { display: "flex", gap: 8, marginTop: 4 },
  whatsappBtn: { padding: "10px 14px", borderRadius: 10, border: "none", background: "#25D366", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  advanceBtn:  { flex: 1, padding: "10px 14px", borderRadius: 10, border: "none", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" },

  // Drawer
  drawerBackdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" },
  drawer:         { background: "#fff", width: "100%", maxWidth: 520, height: "92vh", borderTopLeftRadius: 20, borderTopRightRadius: 20, display: "flex", flexDirection: "column", overflow: "hidden" },
  drawerHeader:   { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "16px 16px 8px", borderBottom: "1px solid #f0f0f0" },
  drawerFooter:   { display: "flex", gap: 8, padding: "12px 16px", borderTop: "1px solid #f0f0f0", background: "#fff", flexShrink: 0 },
  closeBtn:       { width: 32, height: 32, borderRadius: 8, border: "none", background: "#f5f5f3", fontSize: 14, color: "#666", cursor: "pointer" },
  backBtn:        { padding: "11px 16px", border: "1.5px solid #efefed", background: "#fff", borderRadius: 11, fontSize: 13, fontWeight: 600, color: "#666", cursor: "pointer" },
  nextBtn:        { flex: 1, padding: "11px 16px", border: "none", background: "#6366f1", borderRadius: 11, fontSize: 14, fontWeight: 700, color: "#fff", cursor: "pointer" },
  newCustomerBtn: { width: "100%", padding: "10px 14px", marginBottom: 12, borderRadius: 10, border: "1.5px dashed #c7d2fe", background: "#eef2ff", color: "#4338ca", fontSize: 13, fontWeight: 700, cursor: "pointer", textAlign: "left" },

  // Picker rows
  pickerRow:         { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12, border: "1.5px solid #efefed", background: "#fff", marginBottom: 8, cursor: "pointer" },
  pickerRowSelected: { border: "2px solid #6366f1", background: "#f5f3ff" },
  customerAvatar:    { width: 40, height: 40, borderRadius: "50%", background: "#fef3c7", color: "#92400e", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, flexShrink: 0 },
  designThumb:       { width: 48, height: 48, borderRadius: 10, objectFit: "cover", flexShrink: 0 },
  summaryBox:        { background: "#f5f3ff", border: "1.5px solid #c7d2fe", borderRadius: 12, padding: "12px 14px", marginBottom: 16 },

  input: { width: "100%", padding: "10px 12px", border: "1.5px solid #efefed", borderRadius: 10, fontSize: 14, outline: "none", boxSizing: "border-box", background: "#fafaf8", fontFamily: "inherit" },
};
